import { createContext, useContext, useState, useEffect } from "react";
import { ethers } from "ethers";

export const AccessContext = createContext();
export const useAccess = () => useContext(AccessContext);

const CONTRACT_ADDRESS =
  "0xf679a38273547875b1730ae995a1b0d856868843".toLowerCase();
const ALCHEMY_API_KEY = "cBHgm4PNLXlDi1jcTDugv";
const PUBLIC_GATEWAY = "https://funooni-public.mypinata.cloud/ipfs";

export const AccessProvider = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState("");
  const [signature, setSignature] = useState(null); 
  const [accessList, setAccessList] = useState([]);
  const [nfts, setNFTs] = useState([]);
  const [loading, setLoading] = useState(true); 

  const verifyAccess = async () => {
    try {
      if (!window.ethereum) {
        setLoading(false);
        return { accessList: [], nfts: [] };
      }

      setLoading(true);
      
      const accounts = await window.ethereum.request({
        method: "eth_accounts", 
      });

      if (!accounts || accounts.length === 0) {
        setWalletAddress("");
        setAccessList([]);
        setNFTs([]);
        setLoading(false);
        return { accessList: [], nfts: [] };
      }

      const address = accounts[0];
      setWalletAddress(address);

      const url = `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}/getNFTs/?owner=${address}`;
      const response = await fetch(url);
      const data = await response.json();

      const ownedNfts = data?.ownedNfts || [];
      const userContracts = ownedNfts.map((nft) =>
        nft.contract.address.toLowerCase()
      );

      let newAccessList = [];
      let filteredNFTs = [];

      if (userContracts.includes(CONTRACT_ADDRESS)) {
        newAccessList.push(CONTRACT_ADDRESS);

        filteredNFTs = await Promise.all(
          ownedNfts
            .filter(
              (nft) => nft.contract.address.toLowerCase() === CONTRACT_ADDRESS
            )
            .map(async (nft) => {
              let tokenUri = nft.tokenUri?.gateway || nft.tokenUri?.raw;
              if (!tokenUri) return null;
              
              if (tokenUri.startsWith("ipfs://")) {
                tokenUri = `${PUBLIC_GATEWAY}/${tokenUri.replace(
                  "ipfs://",
                  ""
                )}`;
              }

              try {
                const metaRes = await fetch(tokenUri);
                const meta = await metaRes.json();

                return {
                  tokenId: nft.id?.tokenId || nft.tokenId,
                  title:
                    meta?.name || `Token #${nft.id?.tokenId || nft.tokenId}`,
                  description: meta?.description || "",
                  image: meta?.image || meta?.media?.[0]?.gateway || "",
                  accessType:
                    meta?.attributes?.find((a) =>
                      (a.trait_type || a.traitType || a.type || "")
                        .toLowerCase()
                        .includes("access")
                    )?.value || "No Access",
                };
              } catch {
                return {
                  tokenId: nft.id?.tokenId || nft.tokenId,
                  title: `Token #${nft.id?.tokenId || nft.tokenId}`,
                  description: "",
                  image: "",
                  accessType: "No Access",
                };
              }
            })
        );
      }

      setAccessList(newAccessList);
      setNFTs(filteredNFTs);
      return { accessList: newAccessList, nfts: filteredNFTs };
    } catch (e) {
      console.error("verifyAccess Error:", e);
      setWalletAddress("");
      setAccessList([]);
      setNFTs([]);
      return { accessList: [], nfts: [] };
    } finally {
      setLoading(false);
    }
  };
  
  const signMessage = async (message) => {
    if (!window.ethereum || !walletAddress) return null;
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner(walletAddress);
      const sig = await signer.signMessage(message);
      setSignature(sig);
      return sig;
    } catch (e) {
      console.error("Signing failed:", e);
      setSignature(null);
      return null;
    }
  };

  useEffect(() => {
    verifyAccess();
    
    const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
            verifyAccess();
        } else {
            setWalletAddress("");
            setAccessList([]);
            setNFTs([]);
            setSignature(null);
            setLoading(false);
        }
    };

    if (window.ethereum) {
        window.ethereum.on('accountsChanged', handleAccountsChanged);

        return () => {
            window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        };
    }
    
  }, []); 

  return (
    <AccessContext.Provider
      value={{
        walletAddress,
        signature,
        accessList,
        nfts,
        loading,
        verifyAccess, 
        signMessage, 
      }}
    >
      {children}
    </AccessContext.Provider>
  );
};